import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { TbChevronLeft, TbDownload } from "react-icons/tb";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery } from "react-responsive";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import styled, { useTheme } from "styled-components";
import { bookAdminApi } from "../../../app-api/books";
import Button from "../../../components/Button";
import toast from "../../../components/Toast";
import { useTipTap } from "../../../hooks/useExtensions";
import { Body, Bold, Title2, Title3 } from "../../../styles/typography";
import Navbar from "../../book/components/Navbar";

const IS_MOBILE_BREAKPOINT = 500;
const MemoriesContainer = styled.div`
  margin: 0 auto;
  margin-top: 98px;
  padding: 24px;
  background: rgb(${(props) => props.theme.colors.Bgprimary});
  min-height: calc(100vh - 114px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1000px;
  width: 100%;
  svg {
    min-height: 24px;
    min-width: 24px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  button {
    transition: all 150ms ease-in-out;
  }
  @media screen and (max-width: ${IS_MOBILE_BREAKPOINT}px) {
    button {
      width: 50px;
      height: 50px;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  background: rgb(${(props) => props.theme.colors.White});
  box-shadow: 0 2px 8px rgba(${(props) => props.theme.colors.neutral400}, 0.05);
  border-radius: 8px;
  padding: 8px;
  thead,
  th {
    position: sticky;
    top: 0;
    z-index: 2;
  }
`;

const TableHead = styled.thead`
  background: rgb(${(props) => props.theme.colors.Bgtertiary});
`;

const TableHeader = styled(Title3)`
  padding: 1rem;
  text-align: left;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-weight: 600;
  font-size: 1rem;
  background: rgb(${(props) => props.theme.colors.white});
  border-bottom: 2px solid rgb(${(props) => props.theme.colors.neutral150});
`;

const ScrollableTableContainer = styled.div`
  max-height: 600px;
  overflow: auto;
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  padding: 8px;
  padding-top: 0;
  .infinite-scroll-component {
    overflow: unset !important;
  }
`;

const TableRow = styled.tr`
  transition: background 200ms ease-in-out;
  background: rgba(${(props) => props.theme.colors.white});

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background: rgba(${(props) => props.theme.colors.offWhite}, 1);
    }
  }
`;

const TableCell = styled(Body)`
  padding: 1rem;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-size: 1rem;
  border-bottom: 1px solid rgb(${(props) => props.theme.colors.neutral150});
`;

const BookDetailsCard = styled.div`
  width: 100%;
  background: rgb(${(props) => props.theme.colors.White});
  border: 2px solid rgb(${(p) => p.theme.colors.neutral150});
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 32px;
  @media screen and (max-width: ${IS_MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DetailLabel = styled(Bold)`
  color: rgb(${(props) => props.theme.colors.neutral600});
  letter-spacing: 0.02em;
`;

const DetailValue = styled(Body)`
  color: rgb(${(props) => props.theme.colors.neutral400});
  white-space: pre-wrap;
  word-break: break-word;
`;

const EMPTY_VALUE = "—";

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }
  return value;
};

const displayList = (list) => {
  if (!Array.isArray(list) || list.length === 0) {
    return EMPTY_VALUE;
  }
  return list.join(", ");
};

const displayCover = (style, color) => {
  if (!style && !color) return EMPTY_VALUE;
  if (style && color) return `Style ${style} · ${color}`;
  return style || color;
};

const OrderMemories = () => {
  const theme = useTheme();
  const { id } = useParams();
  const location = useLocation();
  const order = location.state?.order;
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [memories, setMemories] = useState([]);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fullName = searchParams.get("name") || "John Doe";
  const totalMemories = searchParams.get("total") ?? null;
  const orderBy = searchParams.get("orderBy") || "recent";
  const isMobile = useMediaQuery({ maxWidth: IS_MOBILE_BREAKPOINT });
  const [isDownloading, setIsDownloading] = useState(false);
  const { getHtml } = useTipTap();
  useEffect(() => {
    const abortController = new AbortController();
    bookAdminApi
      .getOrderMemories({
        orderBy,
        orderID: id,
        signal: abortController.signal,
        pageIndex,
      })
      .then((res) => {
        setHasMore(res.page.currentPage < res.page.totalPages - 1);
        setMemories((prev) => prev.concat(res.data));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort("");
    };
  }, [id, pageIndex]);

  const handleResubmit = () => {
    const abortController = new AbortController();
    setIsDownloading(true);
    bookAdminApi
      .resubmitBook({ orderID: id, signal: abortController.signal })
      .then((response) => {
        toast({
          type: "success",
          message:
            response?.data?.message || "Submission triggered successfully",
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  if (loading) {
    return (
      <SpinnerCircular
        size={48}
        thickness={200}
        speed={100}
        color={`rgba(${theme.colors.neutral200}, 1)`}
        secondaryColor={`rgba(${theme.colors.neutral100}, 1)`}
        style={{
          justifySelf: "center",
          position: "relative",
          left: "45%",
        }}
      />
    );
  }

  return (
    <>
      <Navbar />
      <MemoriesContainer>
        <TitleRow>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <TbChevronLeft
              onClick={() => {
                navigate("/admin/submissions");
              }}
            />
            <Title2 as="h2">
              {fullName}'s Memories{" "}
              {totalMemories ? <>({totalMemories})</> : null}
            </Title2>
          </div>
          <Button
            icon={isMobile}
            variant="primary"
            onButtonClick={handleResubmit}
            loading={isDownloading}
            disabled={isDownloading}
          >
            <TbDownload />
            {!isMobile && <>Download</>}
          </Button>
        </TitleRow>
        {order && (
          <BookDetailsCard>
            <DetailItem>
              <DetailLabel>Book Title</DetailLabel>
              <DetailValue>{displayValue(order.bookTitle)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Subtitle</DetailLabel>
              <DetailValue>{displayValue(order.subtitle)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Author</DetailLabel>
              <DetailValue>
                {displayValue(
                  order.authorFirstName || order.authorLastName
                    ? `${order.authorFirstName ?? ""} ${order.authorLastName ?? ""}`.trim()
                    : order.user?.name
                      ? `${order.user.name.first ?? ""} ${order.user.name.last ?? ""}`.trim()
                      : null,
                )}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Dedication</DetailLabel>
              <DetailValue>{displayValue(order.dedication)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Photo Layout</DetailLabel>
              <DetailValue>{displayValue(order.photoLayout)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Add-ons</DetailLabel>
              <DetailValue>{displayList(order.addOns)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Front Cover</DetailLabel>
              <DetailValue>
                {displayCover(order.frontCoverStyle, order.frontCoverColor)}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Back Cover</DetailLabel>
              <DetailValue>
                {displayCover(order.backCoverStyle, order.backCoverColor)}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Memory Count</DetailLabel>
              <DetailValue>{displayValue(order.memoryCount)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Created At</DetailLabel>
              <DetailValue>
                {order.createdAt
                  ? DateTime.fromFormat(
                      order.createdAt,
                      "yyyy-MM-dd HH:mm:ss",
                      {
                        zone: "UTC",
                      },
                    )
                      .toLocal()
                      .toFormat("MMM dd, yyyy hh:mm a")
                  : EMPTY_VALUE}
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Updated At</DetailLabel>
              <DetailValue>
                {order.updatedAt
                  ? DateTime.fromFormat(
                      order.updatedAt,
                      "yyyy-MM-dd HH:mm:ss",
                      {
                        zone: "UTC",
                      },
                    )
                      .toLocal()
                      .toFormat("MMM dd, yyyy hh:mm a")
                  : EMPTY_VALUE}
              </DetailValue>
            </DetailItem>
          </BookDetailsCard>
        )}
        <ScrollableTableContainer id="scrollableMemories">
          <InfiniteScroll
            dataLength={memories.length}
            next={() => {
              setPageIndex((prev) => prev + 1);
            }}
            hasMore={hasMore}
            scrollThreshold={0.5}
            scrollableTarget="scrollableMemories"
            loader={
              <SpinnerCircular
                size={48}
                thickness={200}
                speed={100}
                color={`rgba(${theme.colors.neutral200}, 1)`}
                secondaryColor={`rgba(${theme.colors.neutral100}, 1)`}
                style={{
                  justifySelf: "center",
                  position: "relative",
                  left: "45%",
                }}
              />
            }
          >
            <Table>
              <TableHead>
                <tr>
                  <TableHeader as="th">S.No</TableHeader>
                  <TableHeader as="th">Memory Title</TableHeader>
                  <TableHeader as="th">Created At</TableHeader>
                  <TableHeader as="th">Updated At</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {memories.map((memory, idx) => (
                  <TableRow style={{ cursor: "pointer" }} key={memory.memoryID}>
                    <Link
                      to={memory.memoryID}
                      style={{ display: "contents" }}
                      state={{ memory }}
                    >
                      <TableCell as="td">{idx + 1}</TableCell>
                      <TableCell as="td">{memory.title}</TableCell>
                      <TableCell as="td">
                        {DateTime.fromISO(memory.createdAt).toFormat(
                          "MMM dd, yyyy",
                        )}
                      </TableCell>
                      <TableCell as="td">
                        {DateTime.fromISO(memory.updatedAt).toFormat(
                          "MMM dd, yyyy",
                        )}
                      </TableCell>
                    </Link>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </InfiniteScroll>
        </ScrollableTableContainer>
      </MemoriesContainer>
    </>
  );
};

export default OrderMemories;
